import { invalidateCursorLists } from "util/cursorListCache";
import axios from "axios";
import { begin, end, presentRetry, isRetryableError } from "util/networkActivity";
import { getActiveTypeHeader, requestSwitchConfirm, notifyRoleRequired } from "common/activeTypeGate";
import { showToast } from "util/appToast";
import { hasNativeBridge } from "util/userAgent";
import { forceLogout } from "common/forceLogout";
import { trackClick } from "util/appAnalytics";

/**
 * 무용연 백엔드 공개 API 클라이언트.
 * baseURL = REACT_APP_API_BASE_URL (배포 시 nginx 가 /api → muyeon-backend 프록시)
 */
const muyeonClient = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  headers: { "Content-Type": "application/json" },
  timeout: 30000, // 30초 리밋 — 초과 시 전역 오버레이가 '다시 시도' 노출
});


// 찜 토글 성공 시 전역 하단 토스트(네이티브식 슬라이드) — 모든 scrap API 공통.
const withScrapToast = (promise, added) =>
  promise.then((res) => {
    showToast(added ? "찜한 목록에 추가했어요." : "찜한 목록에서 삭제했어요.");
    // 이용 행동 로그 — 찜/찜 해제. 대상 종류·ID 는 요청 주소(/teachers/12/scrap)에서 읽는다(모든 찜 API 가 여기를 지난다).
    const m = String(res?.config?.url || "").match(/^\/?([a-z-]+)(?:\/posts)?\/(\d+)\/scrap/);
    if (m) trackClick(`${added ? "like" : "unlike"}:${m[1]}`, { targetId: m[2] });
    return res;
  });

/** 이용 행동 로그 — 성공한 행동(채팅 시작·공고 등록)만 남긴다. 실패·취소는 세지 않는다. */
const trackOnSuccess = (promise, target, idOf) =>
  promise.then((res) => {
    try { trackClick(target, { targetId: idOf ? idOf(res) : undefined }); } catch (e) { /* noop */ }
    return res;
  });

// 로그인 토큰이 있으면 Authorization 자동 첨부 (행동 API용) + 전역 로딩 카운터 시작
//  config.silent=true 면 백그라운드 요청(배지 폴링 등) — 로딩 오버레이·재시도 오버레이 제외.
muyeonClient.interceptors.request.use((config) => {
  try {
    const s = localStorage.getItem("muyeonAuth");
    const token = s ? JSON.parse(s)?.accessToken : null;
    if (token) config.headers.Authorization = `Bearer ${token}`;
  } catch {}
  // ★ 2026-11-04 휴대폰 1 + PC 1 — 앱(웹뷰)·브라우저를 서버가 따로 센다(로그인 토큰 발급 때 사용).
  try { config.headers["X-Muyeon-Client"] = hasNativeBridge() ? "app" : "web"; } catch {}
  // 활성유형(활동 컨텍스트) — accessToken 과 같은 위상으로 매 요청 전달(서버 게이트 판정용)
  config.headers["X-Active-Type"] = getActiveTypeHeader();
  if (!config.silent) { begin(); config.__netCounted = true; }
  return config;
}, (error) => { end(); return Promise.reject(error); });

// 이미지 필드 정규화: 백엔드는 imageUrl(단일)/images(배열) 로 내려주는데
// 프론트 화면은 image/images 를 읽으므로, 응답에서 한 번에 맞춰준다(페이지 무수정).
const normImg = (o) => {
  if (o && typeof o === "object" && !Array.isArray(o)) {
    if (o.imageUrl && !o.image) o.image = o.imageUrl;
    if (Array.isArray(o.images) && o.images.length && !o.image) o.image = o.images[0];
    if (o.imageUrl && !Array.isArray(o.images)) o.images = [o.imageUrl];
  }
  return o;
};
muyeonClient.interceptors.response.use(
  (res) => {
    if (res.config?.__netCounted) end();
    if (Array.isArray(res.data)) res.data.forEach(normImg);
    else normImg(res.data);
    return res;
  },
  async (error) => {
    if (error?.config?.__netCounted) end();
    // 401(토큰 만료/무효) → 세션 정리 + 로그인 유도. 단 로그인 요청 자체는 제외(비번오류 메시지 표시).
    const status = error?.response?.status;
    const url = error?.config?.url || "";
    if (status === 401 && !url.includes("/auth/login") && !url.includes("/auth/kakao") && !url.includes("/auth/naver") && !url.includes("/auth/apple")) {
      // 토큰을 실어 보낸 요청인지(=세션 만료) vs 비회원 요청인지 구분.
      //  비회원은 앱에서도 로그인으로 끌고 가지 않는다(Apple 5.1.1(v): 로그인 없이 둘러보기 보장).
      const hadToken = !!(error?.config?.headers?.Authorization) || (() => {
        try { return !!JSON.parse(localStorage.getItem("muyeonAuth") || "null")?.accessToken; } catch { return false; }
      })();
      // ★ 2026-10-30 다른 기기에서 로그인해 이 토큰이 끝남 — 안내 팝업과 함께 로그인 화면으로(웹·앱 모두).
      if (hadToken && error?.response?.data?.code === "SESSION_REPLACED") {
        forceLogout({ code: "SESSION_REPLACED", message: error.response.data.message });
        return Promise.reject(error);
      }
      try { localStorage.removeItem("muyeonAuth"); } catch {}
      // 웹(브라우저)은 로그인으로 강제이동하지 않고 '로그아웃 상태'로만 전환 → 공개 페이지(홈·목록·상세)
      //  열람 유지(광고 유입자). 인증 필요 라우트만 PrivateRouter 가 로그인으로 보냄.
      //  네이티브 앱은 세션이 만료된 회원만 로그인 화면으로(비회원은 조용히 reject).
      if (hasNativeBridge() && hadToken) {
        const path = window.location.pathname + window.location.search;
        if (!path.startsWith("/login")) {
          window.location.href = "/login?redirect=" + encodeURIComponent(path);
        }
      } else {
        try { window.dispatchEvent(new Event("muyeonForceLogout")); } catch {}
      }
      return Promise.reject(error);
    }
    // 계정 정지·탈퇴(403) — 세션을 정리하고 이유를 알려준 뒤 로그인으로 보낸다.
    //  ⚠️ 종전엔 401 만 처리해서, 사용 중 정지되면 모든 요청이 조용히 403 으로 실패했다.
    //    화면마다 데이터가 안 뜨는데 안내도 로그아웃도 없어 '앱이 고장난' 것처럼 보였다.
    //  ⚠️ 403 은 활성유형 게이트에도 쓰이므로 **코드로** 구분한다(메시지 문자열 매칭 금지).
    const blockedCode = error?.response?.data?.code;
    if (
      status === 403 &&
      (blockedCode === "ACCOUNT_SUSPENDED" || blockedCode === "ACCOUNT_WITHDRAWN")
    ) {
      const msg =
        error?.response?.data?.message ||
        (blockedCode === "ACCOUNT_WITHDRAWN" ? "탈퇴한 계정이에요." : "정지된 계정이에요.");
      // ★ 2026-10-30 alert() 대신 공통 강제 로그아웃 — 세션·네이티브 토큰을 지우고 로그인 화면에서 안내 팝업.
      //  여러 요청이 동시에 403 이어도 한 번만 처리한다(forceLogout 안에서 막음). 정지 회원의 로그인 시도도 같은 팝업.
      forceLogout({ code: blockedCode, message: msg });
      return Promise.reject(error);
    }
    // 활성유형 게이트 — 공통 에러코드 분기(백엔드 docs/active-role-module-plan.md)
    //  SWITCH_REQUIRED: 전환 확인 모달 → 전환 후 같은 요청 1회 자동 재시도(진행 흐름 유지)
    //  ROLE_REQUIRED: 역할 추가/인증 유도 모달(요청 자체는 기존대로 reject)
    const gateBody = error?.response?.data;
    if (gateBody?.code === "SWITCH_REQUIRED" && !error.config?.__switchRetried) {
      const ok = await requestSwitchConfirm({ ...gateBody, request: { url, method: error?.config?.method } });
      if (ok) {
        const cfg2 = { ...error.config, __switchRetried: true };
        cfg2.headers = { ...cfg2.headers, "X-Active-Type": getActiveTypeHeader() };
        return muyeonClient.request(cfg2);
      }
      return Promise.reject(error);
    }
    // ★ 2026-10-12 강사·무용수 추가 시 본인인증 전 회원 — 전역 안내(IdentityRequiredHost)로 본인인증 화면에 보낸다.
    if (status === 409 && gateBody?.code === "IDENTITY_REQUIRED") {
      try { window.dispatchEvent(new Event("muyeonIdentityRequired")); } catch {}
      return Promise.reject(error);
    }
    if (gateBody?.code === "ROLE_REQUIRED") {
      notifyRoleRequired({ ...gateBody, request: { url, method: error?.config?.method } });
      return Promise.reject(error);
    }
    // 타임아웃/네트워크 오류(30초 초과 등) → 전역 오버레이로 '다시 시도' 대기. 재시도 시 같은 요청 재실행.
    //  silent 요청(배지 폴링 등)은 실패해도 조용히 reject — 오버레이로 사용자를 막지 않는다.
    const cfg = error.config;
    if (!cfg?.silent && isRetryableError(error) && cfg) {
      const action = await presentRetry();
      if (action === "retry") return muyeonClient.request(cfg);
    }
    return Promise.reject(error);
  }
);

// MOCK 모드: 콘텐츠 목록/상세 GET 은 백엔드(미완성 시드) 대신 mock 폴백을 쓰도록 즉시 reject.
//  → 페이지의 .catch(() => setMock()) 가 동작해 지역 무관하게 큐레이션 데이터+이미지 노출.
//  (regions/auth 등은 그대로 백엔드 사용)
const USE_MOCK = process.env.REACT_APP_MOCK === "true";
const contentGet = (path, config) =>
  USE_MOCK ? Promise.reject(new Error("MOCK_MODE")) : muyeonClient.get(path, config);

// 활성(대표) 지역 → 쿼리 파라미터
//  sigungu: ?regionCode=11680 (정확 매칭) / sido: ?sido=26 (prefix 매칭)
export const regionParams = (active) =>
  active?.kind === "sigungu"
    ? { regionCode: active.code }
    : active?.kind === "sido"
      ? { sido: active.code }
      : {};

// 인증
export const callRegister = (payload) => muyeonClient.post("/auth/register", payload);
// 이메일 인증(회원가입/비번재설정) — purpose: SIGNUP | RESET
export const callSendEmailCode = (payload) => muyeonClient.post("/auth/email/send-code", payload);
export const callVerifyEmailCode = (payload) => muyeonClient.post("/auth/email/verify-code", payload);
export const callResetPasswordEmail = (payload) => muyeonClient.post("/auth/reset-password-email", payload);
// 토스 일반결제(기간권). checkout = 주문 생성 + 결제창 값, confirm = 승인(여기가 성공해야 결제 완료).
//  자동결제(구독)는 callStartSubscription 쪽이다 — 섞지 말 것.
export const callTossCheckout = (payload) => muyeonClient.post("/monetization/toss/checkout", payload);
// 레슨 예약금 결제. 승인이 성공해야 예약이 PENDING_PAYMENT → CONFIRMED 로 확정된다.
//  멤버십과 승인 경로가 다르다(예약 확정까지 함께 처리) — 섞어 쓰지 말 것.
export const callReservationCheckout = (id) =>
  muyeonClient.post(`/lesson-reservations/${id}/payment/checkout`, {});
export const callReservationPaymentConfirm = (id, payload) =>
  muyeonClient.post(`/lesson-reservations/${id}/payment/confirm`, payload);
export const callTossConfirm = (payload) => muyeonClient.post("/monetization/toss/confirm", payload);
// ★ 2026-11-03 공간 대관 예약 결제 — 예약(POST /spaces/:id/reservation)은 앱이 만들고, 웹은 결제창·승인만.
export const callSpaceReservationCheckout = (id) => muyeonClient.post(`/spaces/reservations/${id}/payment/checkout`, {});
export const callSpaceReservationConfirm = (id, payload) => muyeonClient.post(`/spaces/reservations/${id}/payment/confirm`, payload);
// 멤버십 등급변경. preview 로 "얼마를 더 내고 언제부터 무엇이 되는지"를 먼저 보여주고,
//  checkout → 결제창 → confirm 순으로 진행한다. 금액은 전부 서버가 정한다.
export const callTierChangePreview = (planCode) =>
  muyeonClient.post("/monetization/tier-change/preview", { planCode });
export const callTierChangeCheckout = (planCode) =>
  muyeonClient.post("/monetization/tier-change/checkout", { planCode });
export const callTierChangeConfirm = (id, payload) =>
  muyeonClient.post(`/monetization/tier-change/${id}/confirm`, payload);
export const callGetMyTierChanges = () => muyeonClient.get("/monetization/tier-change/me");
// 내 주문(결제) 내역 — 환불 신청할 주문을 고르는 데 쓴다.
export const callGetMyOrders = () => muyeonClient.get("/monetization/orders/me");
// 멤버십 환불 신청(회원 접수 → 관리자 승인·거절).
export const callCreateRefundRequest = (payload) =>
  muyeonClient.post("/monetization/refund-requests", payload);
export const callGetMyRefundRequests = () => muyeonClient.get("/monetization/refund-requests/me");

// 비로그인 공개 — 요금 안내 화면 + PG 심사 담당자가 보는 경로.
export const callGetPublicPlans = () => muyeonClient.get("/monetization/public/plans");
export const callGetPaymentConfig = () => muyeonClient.get("/monetization/public/payment-config");
// 멤버십 화면 문구(등급 이름·한 줄 소개·추가 혜택·안내) — 서버가 없거나 실패하면 화면 기본 문구를 쓴다.
//  silent: 실패해도 재시도 오버레이를 띄우지 않는다(문구 때문에 구매 화면을 막지 않기 위함).
export const callGetMembershipCopy = () =>
  muyeonClient.get("/monetization/public/membership-copy", { silent: true });
// 멤버십 화면 통합 정보(2026-10-19 관리자 '멤버십 버전') — 등급 문구·서버가 완성한 혜택 문장·안내 문구·적용 버전.
//  구 서버(404)면 위 membership-copy 로 폴백한다(useMembershipCopy).
export const callGetMembershipInfo = () =>
  muyeonClient.get("/monetization/public/membership", { silent: true });

// 휴대폰 본인확인(NICE 통합인증). config = 키 설정·필수 여부, start = 표준창 URL 발급,
//  result = 복귀 후 화면에 보여줄 값(휴대폰은 마스킹됨). 실제 소비는 회원가입 API 가 한다.
export const callNiceIdentityConfig = () => muyeonClient.get("/identity/nice/config");
export const callStartNiceIdentity = (payload) => muyeonClient.post("/identity/nice/start", payload);
// 로그인 회원용 본인확인 시작. 가입 전(start)과 달리 서버가 요청에 회원 id 를 묶어 둔다.
export const callStartNiceIdentityLink = (payload) => muyeonClient.post("/identity/nice/link/start", payload);
// 본인확인 토큰을 소비해 내 이름·휴대폰을 인증 결과로 갱신. 응답은 갱신된 회원 정보.
export const callLinkIdentity = (identityToken) => muyeonClient.post("/auth/me/identity", { identityToken });
export const callGetNiceIdentityResult = (token) => muyeonClient.get("/identity/nice/result", { params: { token } });
export const callAgreeTerms = (agreements) => muyeonClient.post("/auth/terms/agree", { agreements });
export const callLogin = (payload) => muyeonClient.post("/auth/login", payload);
// 카카오 로그인: { code, redirectUri } → 백엔드가 토큰교환·회원매칭 후 자체 JWT 발급
export const callKakaoLogin = (payload) => muyeonClient.post("/auth/kakao", payload);
export const callNaverLogin = (payload) => muyeonClient.post("/auth/naver", payload);
// 애플 로그인: { identityToken, name? } → 백엔드가 토큰검증·회원매칭 후 자체 JWT 발급
export const callAppleLogin = (payload) => muyeonClient.post("/auth/apple", payload);
export const callFindId = (payload) => muyeonClient.post("/auth/find-id", payload);
export const callGetMe = () => muyeonClient.get("/auth/me");
export const callSetActiveType = (type) => muyeonClient.patch("/auth/me/active-type", { type }, { silent: true });
export const callUpdateProfile = (payload) => muyeonClient.patch("/auth/me", payload);
// 소셜 신규회원 추가정보(이름·회원유형) 입력
export const callCompleteSocial = (payload) => muyeonClient.patch("/auth/me/social-complete", payload);
export const callChangePassword = (newPassword) => muyeonClient.patch("/auth/me/password", { newPassword });
export const callDeleteMe = () => muyeonClient.delete("/auth/me");

// 회원탈퇴 — 진입 시 서버가 판정한 값(유형별 사유 목록·정리 필요 내역·삭제 대상 건수)을 받아 화면을 그린다.
//  사유 목록을 서버가 내려주므로 회원유형이 늘어도 프론트는 수정하지 않는다.
export const callGetWithdrawalInfo = () => muyeonClient.get("/auth/me/withdrawal-info");

// 탈퇴 실행 — 본인확인(비밀번호 또는 확인문구) + 사유 + 동의 3종을 한 번에 보낸다.
//  ⚠️ 실패는 HTTP status 가 아니라 응답 body 의 code 로 분기한다(WITHDRAW_BLOCKED / WITHDRAW_AUTH_FAILED …).
//     비밀번호 오류를 401 로 내리지 않는 이유는 위 응답 인터셉터가 401 을 토큰만료로 보고
//     세션을 지워버리기 때문 — 오타 한 번에 로그인이 풀린다.
export const callWithdraw = (payload) => muyeonClient.post("/auth/me/withdraw", payload);

// 강사프로필(공개 프로필) — 회원의 단일 공개 프로필(user.profile).
//  일반회원이 열람하고, 견적요청·채팅이 향하며, 강사상세(/teachers)로 노출된다.
//  canonical: GET/PATCH /auth/me/profile.
export const callGetPublicProfile = () => muyeonClient.get("/auth/me/profile");
// 항목별 공개 범위 { flags: { region, education, ... }, profileHidden } — false = 비공개(공개 프로필에서 빠짐).
export const callGetResumeVisibility = () => muyeonClient.get("/resumes/visibility");
// 항목별 공개 범위 저장 — 바꾼 키만 보내도 된다. careerItems/performanceItems 는 null = 전체 공개.
export const callUpdateResumeVisibility = (payload) => muyeonClient.patch("/resumes/visibility", payload);
export const callUpdatePublicProfile = (payload) => muyeonClient.patch("/auth/me/profile", payload);
// 하위호환 alias — 기존 호출부용(신규 코드는 위 callGetPublicProfile/callUpdatePublicProfile 사용).
export const callGetResume = callGetPublicProfile;
export const callUpdateResume = callUpdatePublicProfile;

// UI 안내 플래그(배너/튜토리얼 확인 여부) — 계정 기준 저장
export const callGetUiFlags = () => muyeonClient.get("/auth/me/ui-flags");
export const callUpdateUiFlags = (flags) => muyeonClient.patch("/auth/me/ui-flags", flags);

// 다중 이력서(resumes, RESUME 전용) — 채용공고/대타/캐스팅 지원 시 첨부하는 구직 문서.
export const callListResumes = (roleIntent) =>
  muyeonClient.get("/resumes", { params: roleIntent ? { roleIntent } : undefined });
export const callGetResumeById = (id) => muyeonClient.get(`/resumes/${id}`);
export const callCreateResume = (payload) => muyeonClient.post("/resumes", payload);
export const callUpdateResumeById = (id, payload) => muyeonClient.patch(`/resumes/${id}`, payload);
export const callDuplicateResume = (id) => muyeonClient.post(`/resumes/${id}/duplicate`);
export const callSetDefaultResume = (id) => muyeonClient.patch(`/resumes/${id}/default`);
export const callDeleteResume = (id) => muyeonClient.delete(`/resumes/${id}`);

// 채팅 — 1:1 방 생성/조회 (공간대관 채팅 버튼 → 공간 소유자와 대화)
// 채팅하기 — 성공하면 cta:chat_start(상대 회원 ID).
export const callCreateDirectRoom = (payload) =>
  trackOnSuccess(muyeonClient.post("/chat/rooms/direct", payload), "cta:chat_start", () => payload?.targetUserId);
// 채팅 — 전체 안읽음 수(플로팅 뱃지용)
export const callChatUnreadCount = () => muyeonClient.get("/chat/unread-count");
// 웹 채팅 — 목록/방/메시지. 실시간 수신은 common/appSocket(Socket.IO /chat).
export const callGetChatRooms = () => muyeonClient.get("/chat/rooms");
export const callGetChatRoomsPage = ({ cursor, limit = 30 } = {}, signal) =>
  muyeonClient.get("/chat/rooms/page", { params: { cursor: cursor || undefined, limit }, signal, silent: true });
export const callGetChatRoom = (id) => muyeonClient.get(`/chat/rooms/${id}`);
export const callGetChatMessages = (id, page = 1, limit = 50) =>
  muyeonClient.get(`/chat/rooms/${id}/messages`, { params: { page, limit } });
// 순번(seq) 커서 조회 — { cursor: "seq" } 최신, { beforeSeq } 이전, { afterSeq } 이후. 응답 { messages, hasMore, lastSeq }(오래된 순).
//  구 서버는 이 파라미터를 무시하고 page 방식 { total, page, limit, messages } 를 돌려준다.
export const callGetChatMessagesBySeq = (id, params) =>
  muyeonClient.get(`/chat/rooms/${id}/messages`, { params });
// 메시지 이모지 반응 토글 { emoji } → { added }. 방에는 서버가 message-reaction 신호를 보낸다.
export const callToggleChatReaction = (roomId, messageId, emoji) =>
  muyeonClient.post(`/chat/rooms/${roomId}/messages/${messageId}/reactions`, { emoji });
// 레슨 전 설문 — 발송 상세(응답 폼/강사 열람). { dispatch, template, questions, levels, response, recipientName, lesson }
export const callGetSurveyDispatch = (dispatchId) => muyeonClient.get(`/surveys/dispatch/${dispatchId}`);
// 레슨 전 설문 응답 제출(수신 회원만). answers = { [문항id]: { optionIds?, etc?, text? }, _level?: {text}, _items?: {optionIds} }
export const callRespondSurvey = (dispatchId, answers, note) =>
  muyeonClient.post(`/surveys/dispatch/${dispatchId}/respond`, note ? { answers, note } : { answers });
// 레슨 전 설문지 목록(강사 '설문지' 시트) — 로그인한 강사면 프로필 장르 설문 + 공통 설문. [{ id, title, genre, description }]
export const callGetSurveyTemplates = () => muyeonClient.get("/surveys/templates");
// 설문지 미리보기 — { template, questions:[{ id, title }], levels }
export const callGetSurveyTemplate = (id) => muyeonClient.get(`/surveys/templates/${id}`);
// 설문지 발송(강사·학원 → 회원) { templateId, roomId, recipientId } — 서버가 SURVEY_CARD 를 만들어 방에 보낸다(앱과 같은 본문).
export const callDispatchSurvey = ({ templateId, roomId, recipientId }) =>
  muyeonClient.post("/surveys/dispatch", { templateId: Number(templateId), roomId: Number(roomId), recipientId: Number(recipientId) });
// 빠른 답변 칩(첫 매칭 직후) — 견적 상담방만 서버가 칩을 준다. [{ id, text, icon }]. 실패는 조용히.
export const callGetChatQuickReplies = (roomId) =>
  muyeonClient.get(`/chat/rooms/${roomId}/quick-replies`, { silent: true });
// AI 답장 추천 칩 { roomId } → { replies: [{ text, source }], intent }. 받을 사람은 서버가 정한다. 실패는 조용히.
export const callGetChatAiReplies = (roomId) =>
  muyeonClient.post("/assistant/chat-replies", { roomId: Number(roomId) }, { silent: true, timeout: 20000 });
// 칩으로 보낸 답 기억 { roomId, intent, text } — 다음 같은 종류 문의에 먼저 보여 준다.
export const callMarkChatAiReplyUsed = (roomId, intent, text) =>
  muyeonClient.post("/assistant/chat-replies/used", { roomId: Number(roomId), intent: intent ?? null, text }, { silent: true });
export const callLeaveChatRoom = (id) => muyeonClient.delete(`/chat/rooms/${id}/leave`);
export const callMuteChatRoom = (id, muted) => muyeonClient.patch(`/chat/rooms/${id}/mute`, { muted });

// 레슨 일정(웹 캘린더 /lessonCalendar) — 앱 LessonCalendarView 와 같은 소스.
//  from/to 없이 호출하면 서버가 전체(내 강사/고객 양쪽) 일정을 준다.
export const callGetLessonSchedules = (from, to) =>
  muyeonClient.get("/lessons/schedules", { params: { from, to } });
// 사용자 캘린더(timetree식 색상) — 일정 칩/컬러바 색에만 쓰이므로 실패해도 화면은 기본색으로 동작.
export const callGetUserCalendars = () => muyeonClient.get("/studio/calendars", { silent: true });

// ★ 2026-11-04 스튜디오 운영(웹 /studio/*) — 앱 StudioService(iOS)·StudioApi(AOS)와 같은 엔드포인트.
//  회원·수강권·매출·설정은 서버가 프로 멤버십이 아니면 403 STUDIO_PRO_REQUIRED, 일정은 레슨 달력과 공용이라 막지 않는다.
//  목록 응답: { items, hasMore, nextCursor }. 화면이 자기 로딩 표시를 그리므로 목록 조회는 silent.
export const callGetStudioMembersPage = ({ q, tab, cursor, limit = 30 } = {}) =>
  muyeonClient.get("/studio/members/page", {
    params: { q: q || undefined, tab: tab && tab !== "all" ? tab : undefined, cursor: cursor || undefined, limit },
    silent: true,
  });
// 회원 상세 — { memberId, name, phone, grade, memo, leadStatus, passes[], recentReservations[] }
export const callGetStudioMember = (id) => muyeonClient.get(`/studio/members/${id}`);
// 메모/등급/상담 상태 { memo?, grade?, leadStatus?: LEAD|ACTIVE }
export const callUpdateStudioMember = (id, body) => muyeonClient.patch(`/studio/members/${id}`, body);
// 수강권 발급 { productName, passType: PERIOD|COUNT, category: GROUP|PRIVATE, startAt, totalCount?, expireAt?, price? }
export const callIssueStudioPass = (memberId, body) => muyeonClient.post(`/studio/members/${memberId}/passes`, body);
// 수강권 연장/잔여/취소 — 앱은 취소({ status: "CANCELED" })만 쓴다.
export const callUpdateStudioPass = (id, body) => muyeonClient.patch(`/studio/passes/${id}`, body);
// 매출 — 요약 { total, unpaidTotal, byCategory[] } / 내역 페이지 / 수동 추가
export const callGetStudioSalesSummary = (from, to) =>
  muyeonClient.get("/studio/sales/summary", { params: { from, to }, silent: true });
export const callGetStudioSalesPage = ({ from, to, cursor, limit = 30 } = {}) =>
  muyeonClient.get("/studio/sales/page", { params: { from, to, cursor: cursor || undefined, limit }, silent: true });
export const callCreateStudioSale = (body) => muyeonClient.post("/studio/sales", body);
// 일정 — { sessions[], blocks[], calendars[] } / 개인일정 추가·삭제
export const callGetStudioSchedule = (from, to) =>
  muyeonClient.get("/studio/schedule", { params: { from, to }, silent: true });
export const callCreateStudioBlock = (body) => muyeonClient.post("/studio/schedule-blocks", body);
export const callDeleteStudioBlock = (id) => muyeonClient.delete(`/studio/schedule-blocks/${id}`);
// 스튜디오 설정 — { noshowConsumes }
export const callGetStudioSettings = () => muyeonClient.get("/studio/settings", { silent: true });
export const callUpdateStudioSettings = (noshowConsumes) =>
  muyeonClient.patch("/studio/settings", { noshowConsumes });

// 레슨 일정 확정/수정(강사·고객 공용) — { startAt, place, placeAddress, memo, calendarId }
export const callSetLessonSchedule = (id, payload) =>
  muyeonClient.patch(`/lessons/schedules/${id}`, payload);

// 레슨 일정 취소·완료 — **강사만**(서버 assertTeacher). 앱 LessonDetailView 의 ••• 메뉴와 같은 API.
//  ⚠️ 예약(source='BOOKING') 일정에는 쓰면 안 된다. 서버가 assertNotBooking 으로 400 을 준다
//     ("예약 레슨은 예약 화면에서만 변경·취소할 수 있어요") → 예약 상세(/reservations/:id)로 보낼 것.
export const callCancelLessonSchedule = (id) => muyeonClient.delete(`/lessons/schedules/${id}`);

// 완료 처리 → 연결된 견적 응답에 완료시각이 찍혀 **고객이 후기를 쓸 수 있게 된다**.
export const callCompleteLessonSchedule = (id) =>
  muyeonClient.patch(`/lessons/schedules/${id}/complete`);

// 완료 되돌리기 — 고객이 후기를 남기기 전까지만 가능(서버가 검증해 거절 메시지를 준다).
export const callUncompleteLessonSchedule = (id) =>
  muyeonClient.patch(`/lessons/schedules/${id}/uncomplete`);

// 변경 이력(확정·수정·취소·완료 로그).
export const callGetLessonScheduleHistory = (id) =>
  muyeonClient.get(`/lessons/schedules/${id}/history`);

// ── 레슨 약속 제안(채팅 카드) ──────────────────────────────────────
//  강사가 채팅으로 날짜를 제안하고 고객이 수락하면 확정 일정이 생긴다.
//  카드는 chat_messages type='LESSON_PROPOSAL' 의 content(JSON)이고,
//  상태가 바뀌면 서버가 content 를 갱신해 message-updated 소켓으로 밀어준다.
//
//  ⚠️ 수락은 409 를 두 가지로 쓴다.
//     · data.conflict 있음 → 내 일정과 겹침. 사용자가 확인하면 force:true 로 재시도 가능
//     · data.conflict 없음 → 강사 일정이 그 사이 겹치게 됨. 재시도해도 안 되니 안내만 한다
export const callAcceptLessonProposal = (id, force = false) =>
  muyeonClient.post(`/lesson-proposals/${id}/accept`, { force });
export const callDeclineLessonProposal = (id) => muyeonClient.post(`/lesson-proposals/${id}/decline`);
export const callCancelLessonProposal = (id) => muyeonClient.post(`/lesson-proposals/${id}/cancel`);
// 제안 보내기 — 앱 LessonProposalService.create 와 같은 본문.
//  { roomId, startAt(ISO), durationMin, force, place?, placeAddress?, placeLat?, placeLng?, memo?, calendarId? }
//  409 + conflict → 그 시간에 일정이 있음. 사용자가 확인하면 force:true 로 다시 보낸다.
export const callCreateLessonProposal = (payload) => muyeonClient.post("/lesson-proposals", payload);
// 확정된 약속 회원 취소 — 먼저 예상 환불액(서버 계산)을 받고, 확인하면 취소한다.
//  cancel-preview → { paidAmount, expectedRefundAmount, ... }
export const callLessonProposalCancelPreview = (id) => muyeonClient.get(`/lesson-proposals/${id}/cancel-preview`);
export const callMemberCancelLessonProposal = (id) => muyeonClient.post(`/lesson-proposals/${id}/member-cancel`);
export const callLessonProposalCheckout = (id) => muyeonClient.post(`/lesson-proposals/${id}/payment/checkout`, {});
export const callLessonProposalPaymentConfirm = (id, payload) => muyeonClient.post(`/lesson-proposals/${id}/payment/confirm`, payload);

// ── 강사용: 예약 시간(슬롯) 관리 · 출석 ─────────────────────────────
//  앱 LessonSlotManageView 와 같은 API. 개설 강사·관리 학원만 호출 가능(서버가 소유자 검증).
//
//  구조: 반복 규칙(템플릿)을 만들면 서버가 회차(슬롯 인스턴스)를 자동 생성한다.
//        회차를 직접 만드는 API 는 없다 — 규칙만 관리한다.
export const callGetSlotTemplates = (lessonProductId) =>
  muyeonClient.get("/lesson-slot-templates", { params: { lessonProductId } });

// dayOfWeek 0=일 ~ 6=토, startTime/endTime 은 "HH:mm", capacity>=1.
//  weeksAhead(1~24) 는 몇 주치 회차를 미리 만들지. 생략하면 서버 기본값.
export const callCreateSlotTemplate = (payload) =>
  muyeonClient.post("/lesson-slot-templates", payload);

export const callDeleteSlotTemplate = (id) =>
  muyeonClient.delete(`/lesson-slot-templates/${id}`);

// 관리용 회차 목록(마감·정원 찬 것 포함). 예약용 available-slots 와 다르다.
export const callGetProductSlots = (lessonProductId, from, to) =>
  muyeonClient.get(`/lesson-products/${lessonProductId}/slots`, { params: { from, to } });

// 회차 예약자 명단 — 출석 화면용. 취소 건은 빠지고 회원별 보유 수강권도 함께 온다.
export const callGetSlotReservations = (slotId) =>
  muyeonClient.get(`/lesson-slots/${slotId}/reservations`);

export const callDecideReservationReschedule = (reservationId, approved) =>
  muyeonClient.patch(`/lesson-reservations/${reservationId}/reschedule-decision`, { approved });

// 출석 처리 — status: CONFIRMED(되돌리기) | ATTENDED | NOSHOW.
//  passId 를 주면 그 수강권에서 차감한다(서버가 차감·환원을 관리).
export const callSetAttendance = (slotId, reservationId, status, passId) =>
  muyeonClient.patch(`/lesson-slots/${slotId}/reservations/${reservationId}/attendance`,
    passId ? { status, passId } : { status });

// 디바이스(FCM) 토큰 등록 — muyeonClient(=muyeonAuth JWT) 로 호출해야 userId 가 연결됨.
export const callSaveDevice = (payload) => muyeonClient.post("/user/account/saveDeviceInfo", payload);

// 알림 수신 설정(종류별 푸시 on/off) — 알림함 칩과 같은 카테고리 표를 쓴다.
//  ⚠️ '관심 조건 알림'(me/alert-prefs)과 다른 API 다. 저쪽은 어떤 공고를 받을지,
//     이쪽은 받은 알림을 푸시로 보낼지.
export const callGetNotificationPrefs = () => muyeonClient.get("/me/notification-prefs");
export const callUpdateNotificationPrefs = (payload) =>
  muyeonClient.patch("/me/notification-prefs", payload);

// 알림함 (N3)
//  category: 종류별 보기(LESSON/QUOTE/JOB/...). 값의 정의·라벨·노출순서는 서버가 갖는다
//  (callGetNotiCategories) — 앱·웹·AOS 가 각자 하드코딩하면 그 순간부터 어긋난다.
export const callGetNotifications = ({ category } = {}) =>
  muyeonClient.get("/notifications", {
    params: { ...(category && category !== "ALL" ? { category } : {}) },
  });
export const callNotiUnreadCount = () => muyeonClient.get("/notifications/unread-count", { silent: true });
// 알림 카테고리 칩 — 내 활동유형(X-Active-Type 인터셉터) 기준 노출셋 + 종류별 건수.
//  ⚠️ 실패해도 화면이 비면 안 된다. 호출부는 칩 없이 목록만 그린다(app-menus 와 같은 규칙).
export const callGetNotiCategories = () =>
  muyeonClient.get("/notifications/categories", { silent: true });
// 내 프로필 열람 통계(누적 열람 횟수·고유 열람자) — 지원내역 화면 상단 표시.
export const callGetMyProfileViewStats = () => muyeonClient.get("/me/profile-view-stats", { silent: true });
// 받은견적 안읽음 수(보면 사라지는 배지) — 백그라운드 조회라 silent.
export const callGetQuoteUnreadCount = () => muyeonClient.get("/quotes/unread-count", { silent: true });
export const callMarkNotiRead = (id) => muyeonClient.patch(`/notifications/${id}/read`);
// 모두 읽음. 종류 필터가 걸려 있으면 그 종류만 읽는다(보고 있지 않은 알림까지 읽히면 잃는다).
export const callMarkAllNotiRead = (category) =>
  muyeonClient.patch("/notifications/read-all", {}, {
    params: category && category !== "ALL" ? { category } : {},
  });

// 회원 유형 추가 (멀티 유형 보유 — 권한은 보유 유형 합집합)
export const callAddMemberType = (memberType) =>
  muyeonClient.post("/auth/me/member-types", { memberType });

// 이미지 업로드 (multipart) — 공통.
//  POST /uploads/image (JWT 자동 첨부, 단일 field 'file') → { url } 응답에서 url 문자열 반환.
//  업로드 실패 시 throw (호출부에서 .catch 로 처리). MOCK 모드 폴백은 호출부에서 처리.
export const callUploadImage = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  const res = await muyeonClient.post("/uploads/image", fd, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 60000, // 사진(수 MB) 업로드는 기본 10초로 부족 → 60초
  });
  return res?.data?.url || null;
};

// ★ 2026-10-31 뱃지 묶음 — { notifications, chat, quotes, total, categories, typeChips } (하단 MY 탭 점·다른 유형 말풍선).
export const callGetBadges = () => muyeonClient.get("/assistant/badges");

// ★ 2026-10-31 캐스팅 자료 업로드(무용연 운영 계정 전용) — 인스타 캡처 묶음 → 서버 AI 가 캐스팅 공고로 등록.
export const callCastingMaterialAccess = () => muyeonClient.get("/casting-materials/access");
export const callListCastingMaterials = (kind = "CASTING") => muyeonClient.get("/casting-materials", { params: { kind } });
export const callCreateCastingMaterial = (body) => muyeonClient.post("/casting-materials", body);

// 동영상 업로드 — POST /uploads/video (field 'file', video/*, 서버 한도 60MB) → { url }. 채팅 VIDEO 메시지용.
export const callUploadVideo = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  const res = await muyeonClient.post("/uploads/video", fd, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 180000, // 수십 MB 영상은 모바일 회선에서 오래 걸린다
  });
  return res?.data?.url || null;
};

// ★ 2026-10-12 확인 서류 업로드(비공개) — POST /uploads/document → { key }. 공개 주소가 없어 미리보기는 로컬 파일로 한다.
export const callUploadDocument = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  const res = await muyeonClient.post("/uploads/document", fd, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 60000,
  });
  return res?.data?.key || null;
};

// ★ 2026-10-12 전문 정보 확인(전공·경력·자격·지도경력) — 내 현황 / 항목 서류 제출
export const callGetMyCredentials = () => muyeonClient.get("/me/credentials");
export const callSubmitCredential = (kind, keys) => muyeonClient.post(`/me/credentials/${kind}`, { keys });

// 지역
export const callGetRegions = () => muyeonClient.get("/regions");

// 현재 좌표에서 가장 가까운 지역(시군구)
export const callGetNearestRegion = (lat, lng) =>
  muyeonClient.get("/regions/nearest", { params: { lat, lng } });

// 홈 통합 피드
export const callGetHomeFeed = (active) =>
  contentGet("/home/feed", { params: regionParams(active) });
export const callGetHomeFeedPage = (active, filters = {}, signal) =>
  contentGet("/home/feed/page", { params: { ...regionParams(active), ...filters }, signal, silent: true });

// 회원유형 선택(네이티브) 상단 히어로 배경 이미지 목록(최대 3장, 랜덤용)
export const callGetRoleHeroImages = () => contentGet("/home/role-hero");

// 강사채용 (지역 + 경력/급여 필터)
//  filters: { career?: 'Y3_5', salary?: 'W5_6' } - 'ALL'/빈값은 제외
export const callGetJobs = (active, filters = {}) => {
  const params = { ...regionParams(active) };
  if (filters.career && filters.career !== "ALL") params.career = filters.career;
  if (filters.salary && filters.salary !== "ALL") params.salary = filters.salary;
  // 일정 성격(하루/단기/정기)·최소 타임 수 — 2026-09-19 서버 개편분.
  if (filters.scheduleType && filters.scheduleType !== "ALL") params.scheduleType = filters.scheduleType;
  if (filters.minClassCount && filters.minClassCount !== "ALL") params.minClassCount = filters.minClassCount;
  if (filters.field && filters.field !== "ALL") params.field = filters.field;
  return contentGet("/jobs", { params });
};
export const callGetJobsPage = (active, filters, signal) =>
  contentGet("/jobs/page", { params: { ...regionParams(active), ...filters }, signal, silent: true });
export const callGetJob = (id) => contentGet(`/jobs/${id}`);
// 비슷한 공고·나에게 맞는 공고 — 상세/목록 아래 보조 영역이라 실패해도 화면을 막지 않는다(silent).
//  서버가 아직 없는 경로(구 서버 404)여도 호출부에서 빈 목록으로 처리한다.
export const callGetSimilarJobs = (id, limit = 6) =>
  contentGet(`/jobs/${id}/similar`, { params: { limit }, silent: true });
export const callGetRecommendedJobs = (limit = 10) =>
  contentGet("/jobs/recommended-for-me", { params: { limit }, silent: true });
export const callCreateJob = (payload) => trackOnSuccess(muyeonClient.post("/jobs", payload), "cta:posting_create_job", (r) => r?.data?.id);
// 인증 대기 공고 — 인증 서류를 제출한(심사 중) 회원의 채용공고를 비공개로 저장, 승인 시 자동 공개.
//  body = 일반 등록 body + role('ACADEMY'|'TEAM') + landing(광고 유입 기록, 선택). 응답 { id, pending }.
export const callCreatePendingJob = (payload) => muyeonClient.post("/jobs/pending", payload);
export const callUpdateJob = (id, payload) => muyeonClient.patch(`/jobs/${id}`, payload);
export const callApplyJob = (id, body = {}) => muyeonClient.post(`/jobs/${id}/apply`, body);
// 내가 이 공고에 이미 지원했는지 — 지원 버튼 상태용.
export const callCheckJobApplied = (id) => muyeonClient.get(`/jobs/${id}/applied`);
// 공고 열람 기록 + 내가 본 공고 id 목록 — 구인 목록 '본 공고' 표시용.
export const callRecordJobView = (id) => muyeonClient.post(`/jobs/${id}/view`);
export const callGetMyViewedJobs = () => muyeonClient.get(`/jobs/views/mine`);
// 지원자 목록(공고 등록자 본인만) — 지원자명 + 첨부 이력서.
export const callGetJobApplicants = (id) => muyeonClient.get(`/jobs/${id}/applicants`);
// 지원자 1명 상세(이력서 풀필드, 공고 등록자 본인).
export const callGetJobApplicant = (jobId, applicationId) =>
  muyeonClient.get(`/jobs/${jobId}/applicants/${applicationId}`);
// 받은 지원 통합(내 모든 공고의 지원자, 원장 본인).
export const callGetReceivedApplicationsPage = (params = {}, signal) =>
  muyeonClient.get("/me/received-applications/page", { params: { limit: 30, ...params }, signal });
export const callGetReceivedApplications = () =>
  muyeonClient.get(`/jobs/applications/received`);
export const callArchiveReceivedApplication = (kind, applicationId) =>
  muyeonClient.delete(`/me/received-applications/${kind}/${applicationId}`);
export const callRestoreReceivedApplication = (kind, applicationId) =>
  muyeonClient.post(`/me/received-applications/${kind}/${applicationId}/restore`);
export const callPurgeReceivedApplication = (kind, applicationId) =>
  muyeonClient.delete(`/me/received-applications/${kind}/${applicationId}/permanent`);
// 지원자 채용 확정/지원 종료 처리(공고 등록자 본인) — status: 'ACCEPTED' | 'REJECTED'.
export const callDecideJobApplication = (applicationId, status) =>
  muyeonClient.patch(`/jobs/applications/${applicationId}/status`, { status });
export const callDecideSubApplication = (applicationId, status) =>
  muyeonClient.patch(`/subs/applications/${applicationId}/status`, { status });
export const callGetPendingApplicantCount = (kind, id) =>
  muyeonClient.get(`/${kind === "SUB" ? "subs" : "jobs"}/${id}/pending-applicants/count`);

// 대타공고 (지역 + 분야/대상/급여/마감/날짜 필터)
//  filters: { field?, target?, salary?, open?: true, date? } - 'ALL'/빈값 제외
export const callGetSubs = (active, filters = {}) => {
  const params = { ...regionParams(active) };
  if (filters.field && filters.field !== "ALL") params.field = filters.field;
  if (filters.target && filters.target !== "ALL") params.target = filters.target;
  if (filters.salary && filters.salary !== "ALL") params.salary = filters.salary;
  // 일정 성격(하루/단기/정기)·최소 타임 수 — 2026-09-19 서버 개편분.
  if (filters.scheduleType && filters.scheduleType !== "ALL") params.scheduleType = filters.scheduleType;
  if (filters.minClassCount && filters.minClassCount !== "ALL") params.minClassCount = filters.minClassCount;
  // 내 이력서(장르·수업대상)로 서버에서 거른다. 로그인 상태에서만 의미가 있다
  //  — 토큰이 없으면 백엔드가 그냥 전체를 준다(OptionalJwtGuard).
  if (filters.applyPrefs) params.applyPrefs = true;
  if (filters.open) params.open = true; // 마감 제외
  if (filters.date) params.date = filters.date;
  return contentGet("/subs", { params });
};
export const callGetSub = (id) => contentGet(`/subs/${id}`);
export const callGetSimilarSubs = (id, limit = 6) =>
  contentGet(`/subs/${id}/similar`, { params: { limit }, silent: true });
export const callGetSubApplicants = (id) => muyeonClient.get(`/subs/${id}/applicants`);
export const callGetReceivedSubApplications = () => muyeonClient.get("/subs/applications/received");
export const callGetSubApplicant = (subId, applicationId) =>
  muyeonClient.get(`/subs/${subId}/applicants/${applicationId}`);
export const callGetReceivedCastingApplications = () => muyeonClient.get("/casting/applications/received");
export const callGetCastingApplicant = (callId, applicationId) =>
  muyeonClient.get(`/casting/${callId}/applicants/${applicationId}`);
export const callCreateSub = (payload) => trackOnSuccess(muyeonClient.post("/subs", payload), "cta:posting_create_sub", (r) => r?.data?.id);
// 인증 대기 대타공고 — role('TEACHER'|'ACADEMY') + landing. 응답 { id, pending }(pending=false 면 이미 인증돼 바로 공개).
export const callCreatePendingSub = (payload) => muyeonClient.post("/subs/pending", payload);
export const callUpdateSub = (id, payload) => muyeonClient.patch(`/subs/${id}`, payload); // 수정(작성자만)
export const callDeleteSub = (id) => muyeonClient.delete(`/subs/${id}`); // 삭제(소프트 — 노출 차단)
export const callApplySub = (id, body = {}) => muyeonClient.post(`/subs/${id}/apply`, body);
export const callCheckSubApplied = (id) => muyeonClient.get(`/subs/${id}/applied`); // 이미 지원한 공고인지
export const callScrapSub = (id) => withScrapToast(muyeonClient.post(`/subs/${id}/scrap`), true);
export const callUnscrapSub = (id) => withScrapToast(muyeonClient.delete(`/subs/${id}/scrap`), false);
export const callCloseSub = (id, rejectPending = false) =>
  muyeonClient.patch(`/subs/${id}/close`, { rejectPending });

// ── 대타 긴급 발송(타임당 금액 자동 인상 재발송) · 확정 취소 패널티 ──
//  발송·인상·중지 = 공고 올린 사람(유료 멤버십). 수락·철회 = 강사·무용수(무료).
// ★ 2026-10-05 금액 올리기(callRaiseSubDispatch) 폐지 — 금액은 공고 수정(callUpdateSub)으로 바꾼다.
export const callStopSubDispatch = (id) => muyeonClient.post(`/subs/${id}/dispatch/stop`);
// 예상 수신 강사 수 — 등록 화면에서 조건이 바뀔 때마다(디바운스) 부른다. 로딩 오버레이 없이.
export const callPreviewSubDispatch = (params) =>
  muyeonClient.get("/subs/dispatch/preview", { params, silent: true });
export const callAcceptSubOffer = (id, body = {}) => muyeonClient.post(`/subs/${id}/accept-offer`, body);
export const callWithdrawSubOffer = (id) => muyeonClient.delete(`/subs/${id}/accept-offer`);
// 확정 후 취소 — 미리보기(점수·남은 시간) → 취소. 노쇼 신고는 올린 사람이 수업 시작 후.
export const callGetSubCancelPreview = (applicationId) =>
  muyeonClient.get(`/subs/applications/${applicationId}/cancel-preview`);
export const callCancelSubApplication = (applicationId, reason) =>
  muyeonClient.post(`/subs/applications/${applicationId}/cancel`, reason ? { reason } : {});
export const callReportSubNoShow = (applicationId) =>
  muyeonClient.post(`/subs/applications/${applicationId}/noshow`);
export const callGetMySubTrust = () => muyeonClient.get("/me/sub-trust");
export const callGetSubDispatchPrefs = () => muyeonClient.get("/me/sub-dispatch-prefs");
export const callUpdateSubDispatchPrefs = (payload) => muyeonClient.put("/me/sub-dispatch-prefs", payload);

export const callGetSeekersPage = (filters = {}, signal) => contentGet("/seekers/page", {
  params: { ...filters, cursor: filters.cursor || undefined, limit: filters.limit ?? 30 }, silent: true, signal,
});

// 강사 이력서 열람 (학원/공연팀) — 미리보기 목록 + 상세(권한)
//  filters: { field?, region?, hasVideo?: true, sort?: 'recent'|'career', lesson?: true }
//
//  ⚠️ lesson: 강사가 '강사 탭에 노출'(profile.lessonEnabled)을 켠 사람만 남긴다.
//   서버(profiles.service listTeachers)는 `?lesson=true|1` 이 **올 때만** 필터하고,
//   파라미터가 없으면 전원 통과시킨다. 즉 이 줄이 빠지면 토글을 꺼도 그대로 노출된다
//   (실제로 그렇게 새고 있었음 — LessonTab 은 lesson:1 을 넘겼는데 여기서 버려졌다).
//   소비자용 목록(강사 탭 /lessons, 홈 '좋아할 강사')에서만 넘기고,
//   채용 목적 목록(구직 탭·강사 찾아보기·학원 추천)은 넘기지 않는다 —
//   레슨을 안 해도 채용 대상으로는 검색돼야 하므로.
const teacherParams = (filters = {}) => {
  const params = {};
  if (filters.field && filters.field !== "ALL") params.field = filters.field;
  if (filters.genre && filters.genre !== "ALL") params.genre = filters.genre; // 구직 분야(한글 장르)
  if (filters.target && filters.target !== "ALL") params.target = filters.target; // 구직 지도대상
  if (filters.region) params.region = filters.region;
  if (filters.regionKind) params.regionKind = filters.regionKind;
  if (filters.hasVideo) params.hasVideo = true;
  if (filters.hasCert) params.hasCert = true;
  if (filters.credential) params.credential = "true"; // ★ 2026-10-12 전문 정보 확인 배지가 있는 강사만
  if (filters.sort) params.sort = filters.sort;
  if (filters.lesson) params.lesson = "true"; // 서버가 문자열 'true'|'1' 만 인식
  return params;
};
export const callGetTeachers = (filters = {}) => contentGet("/teachers", { params: teacherParams(filters) });
export const callGetTeachersPage = (filters = {}, signal) => contentGet("/teachers/page", {
  params: { ...teacherParams(filters), limit: filters.limit ?? 30, cursor: filters.cursor || undefined }, signal, silent: true,
});
export const callGetTeacher = (id, src) => contentGet(`/teachers/${id}${src ? `?src=${src}` : ""}`); // 상세(서버 권한 재검증, src=chat|quote 는 열람 알림 제외)
export const callGetAcademy = (id, src) => contentGet(`/academies/${id}${src ? `?src=${src}` : ""}`); // 거래 성립 시에만 연락처 포함
// 공고 상세 안 '학원 정보' 요약용 — 보조 영역이라 로딩·재시도 오버레이 없이 조용히 부른다(src=jobs: 연락처 제외).
export const callGetAcademySummary = (id) => contentGet(`/academies/${id}?src=jobs`, { silent: true });
export const callScrapTeacher = (id) => withScrapToast(muyeonClient.post(`/teachers/${id}/scrap`), true);
export const callUnscrapTeacher = (id) => withScrapToast(muyeonClient.delete(`/teachers/${id}/scrap`), false);

// 무용수 캐스팅
export const callGetCastings = (active) =>
  contentGet("/casting", { params: regionParams(active) });
export const callGetCastingsPage = ({ active, genre, feeRange, cursor } = {}, signal) =>
  contentGet("/casting/page", { params: { ...regionParams(active), genre, feeRange, cursor, limit: 30 }, signal, silent: true });
export const callGetCasting = (id) => contentGet(`/casting/${id}`);
export const callGetSimilarCastings = (id, limit = 6) =>
  contentGet(`/casting/${id}/similar`, { params: { limit }, silent: true });
export const callCreateCasting = (payload) => trackOnSuccess(muyeonClient.post("/casting", payload), "cta:posting_create_casting", (r) => r?.data?.id);
// ★ 2026-10-31 공고 수정(작성자) — 바꿀 칸만 보낸다.
export const callUpdateCasting = (id, body) => muyeonClient.patch(`/casting/${id}`, body);
export const callApplyCasting = (id, body = {}) => muyeonClient.post(`/casting/${id}/apply`, body);
export const callCheckCastingApplied = (id) => muyeonClient.get(`/casting/${id}/applied`);
export const callRecordCastingView = (id) => muyeonClient.post(`/casting/${id}/view`);
export const callGetMyViewedCastings = () => muyeonClient.get(`/casting/views/mine`);

// 공연(관람) — 일반회원이 보러 갈 공연 정보. 관리자/공연팀 등록.
export const callGetPerformances = (params = {}) =>
  contentGet("/performances", { params });
export const callGetPerformancesPage = ({ genre, subGenre, tag, cursor, limit = 30 } = {}, signal) =>
  contentGet("/performances/page", { silent: true, signal,
    params: { ...(genre && genre !== "ALL" ? { genre } : {}), ...(subGenre ? { subGenre } : {}), ...(tag ? { tag } : {}),
      ...(cursor ? { cursor } : {}), limit } });
export const callGetPerformance = (id) => contentGet(`/performances/${id}`);
// ★ 10-26 '캐스팅·무대' 주간 무대 소식(관리자 서버가 매주 월 07:45 수집). 호가 아직 없으면 latest 는 null.
export const callGetStageNewsLatest = (signal) => contentGet("/stage-news/latest", { signal, silent: true });
export const callGetStageNewsIssues = () => contentGet("/stage-news/issues", { silent: true });
export const callGetStageNewsIssue = (issueNo, signal) => contentGet(`/stage-news/issues/${issueNo}`, { signal, silent: true });
export const callCreatePerformance = (payload) => trackOnSuccess(muyeonClient.post("/performances", payload), "cta:posting_create_performance", (r) => r?.data?.id);
export const callUpdatePerformance = (id, payload) => muyeonClient.patch(`/performances/${id}`, payload);
export const callDeletePerformance = (id) => muyeonClient.delete(`/performances/${id}`);

// 무용수 프로필 열람 (공연팀) — 미리보기 목록 + 상세(권한)
//  filters: { major?, gender?, hasVideo?: true, hasAward?: true }
const dancerParams = (filters = {}) => {
  const params = {};
  if (filters.major && filters.major !== "ALL") params.major = filters.major;
  if (filters.target && filters.target !== "ALL") params.target = filters.target; // 구직 지도대상
  if (filters.gender && filters.gender !== "ALL") params.gender = filters.gender;
  if (filters.hasVideo) params.hasVideo = true;
  if (filters.hasAward) params.hasAward = true;
  return params;
};
export const callGetDancers = (filters = {}) => contentGet("/dancers", { params: dancerParams(filters) });
export const callGetDancersPage = (filters = {}, signal) => contentGet("/dancers/page", {
  params: { ...dancerParams(filters), limit: filters.limit ?? 30, cursor: filters.cursor || undefined }, signal, silent: true,
});
export const callGetDancer = (id) => contentGet(`/dancers/${id}`); // 상세(서버 권한 재검증)
export const callScrapDancer = (id) => withScrapToast(muyeonClient.post(`/dancers/${id}/scrap`), true);
export const callOfferDancer = (id, body = {}) => muyeonClient.post(`/dancers/${id}/offer`, body); // 캐스팅 제안

// 공간대관
export const callGetSpaceCategories = () => contentGet("/spaces/categories");
export const callGetSpaces = (active, { categoryId, date } = {}) => {
  const params = { ...regionParams(active) };
  if (categoryId && categoryId !== "ALL") params.categoryId = categoryId;
  if (date) params.date = date;
  return contentGet("/spaces", { params });
};
export const callGetSpace = (id) => contentGet(`/spaces/${id}`);
// ★ 2026-11-03 무용센터 양도양수 목록(홈 섹션) — { items, hasMore, nextCursor }
export const callGetStudioTransfers = (params = {}) => contentGet("/studio-transfers/page", { params });
export const callCreateSpace = (payload) => muyeonClient.post("/spaces", payload);

// ★ 2026-11-03 무용센터 양도양수 웹 화면 — 목록·상세·내 글·등록/수정·상태·삭제·찜·이용약관 동의(서버 저장).
//  목록·상세는 화면이 스켈레톤을 그리므로 전역 로딩 오버레이 없이(silent) 부른다.
export const callGetStudioTransferPage = (params = {}, signal) =>
  contentGet("/studio-transfers/page", { params, signal, silent: true });
export const callGetStudioTransfer = (id) => contentGet(`/studio-transfers/${id}`, { silent: true });
export const callGetMyStudioTransfers = () => muyeonClient.get("/studio-transfers/mine", { silent: true });
export const callCreateStudioTransfer = (payload) => muyeonClient.post("/studio-transfers", payload);
export const callUpdateStudioTransfer = (id, payload) => muyeonClient.patch(`/studio-transfers/${id}`, payload);
export const callSetStudioTransferStatus = (id, status) => muyeonClient.patch(`/studio-transfers/${id}/status`, { status });
export const callDeleteStudioTransfer = (id) => muyeonClient.delete(`/studio-transfers/${id}`);
export const callGetTransferConsent = () => muyeonClient.get("/studio-transfers/consent", { silent: true });
export const callAgreeTransferConsent = (source) => muyeonClient.post("/studio-transfers/consent", { source });
export const callScrapStudioTransfer = (id) => withScrapToast(muyeonClient.post(`/studio-transfers/${id}/scrap`), true);
export const callUnscrapStudioTransfer = (id) => withScrapToast(muyeonClient.delete(`/studio-transfers/${id}/scrap`), false);

// ★ 2026-11-03 공간 대관 웹 화면 — 목록(지역·날짜·인원·정렬)·상세·예약 가능 시간·호스트 동의·등록/수정·예약 관리.
export const callGetSpaceList = (params = {}) => contentGet("/spaces", { params, silent: true });
export const callGetSpaceDetail = (id) => contentGet(`/spaces/${id}`, { silent: true });
export const callGetSpaceAvailability = (id, date, optionId) =>
  muyeonClient.get(`/spaces/${id}/availability`, { params: { date, optionId: optionId || undefined }, silent: true });
export const callGetSpaceRefundPresets = () => muyeonClient.get("/spaces/refund-presets", { silent: true });
export const callGetSpaceHostConsent = () => muyeonClient.get("/spaces/host-consent", { silent: true });
export const callAgreeSpaceHostConsent = () => muyeonClient.post("/spaces/host-consent", {});
export const callUpdateSpace = (id, payload) => muyeonClient.patch(`/spaces/${id}`, payload);
export const callDeleteSpace = (id) => muyeonClient.delete(`/spaces/${id}`);
export const callGetHostSpaces = () => muyeonClient.get("/spaces/mine", { silent: true });
export const callGetMySpaceBookings = () => muyeonClient.get("/spaces/reservations/mine", { silent: true });
export const callGetHostingReservations = () => muyeonClient.get("/spaces/reservations/hosting", { silent: true });
export const callSpaceCancelPreview = (resId) => muyeonClient.get(`/spaces/reservations/${resId}/cancel-preview`);
export const callCancelSpaceReservation = (resId, reason) =>
  muyeonClient.post(`/spaces/reservations/${resId}/cancel`, reason ? { reason } : {});
export const callHostCancelSpaceReservation = (resId, reason) =>
  muyeonClient.post(`/spaces/reservations/${resId}/host-cancel`, reason ? { reason } : {});
// ★ 2026-11-04 수락 후 확정 — 호스트 수락(확정) / 거절(전액 환불·취소)
export const callAcceptSpaceReservation = (resId) => muyeonClient.post(`/spaces/reservations/${resId}/accept`, {});
export const callDeclineSpaceReservation = (resId, reason) =>
  muyeonClient.post(`/spaces/reservations/${resId}/decline`, reason ? { reason } : {});
// 장소·주소 검색(카카오 로컬, 로그인 회원만) → { places:[{name,roadAddress,jibunAddress,latitude,longitude}], hasMore }
export const callSearchPlaces = (query, page = 1) =>
  muyeonClient.get("/places/search", { params: { query, page }, silent: true });
export const callInquireSpace = (id, body = {}) => muyeonClient.post(`/spaces/${id}/inquiry`, body);
export const callReserveSpace = (id, body = {}) => muyeonClient.post(`/spaces/${id}/reservation`, body);

// 취미무용 클래스
export const callGetClasses = (active) =>
  contentGet("/classes", { params: regionParams(active) });
export const callGetClass = (id) => contentGet(`/classes/${id}`);
// 클래스 상담/등록 문의 — 백엔드 @Body('message') (단일 message 필드)
export const callPostClassInquiry = (classId, body = {}) =>
  muyeonClient.post(`/classes/${classId}/inquiry`, body);

// 취미무용 커뮤니티 (Phase 6)
//  category 'ALL'/빈값은 전체, sort: 'popular'|'recent'
export const callGetPosts = (params = {}) => {
  const q = {};
  if (params.category && params.category !== "ALL") q.category = params.category;
  if (params.sort) q.sort = params.sort;
  return contentGet("/community/posts", { params: q });
};
export const callGetPostsPage = (params = {}, signal) =>
  contentGet("/community/posts/page", { params, signal, silent: true });
export const callGetPostSummary = (id, signal) => contentGet(`/community/posts/${id}`, { params: { comments: 0 }, signal, silent: true });
export const callGetCommentsPage = (id, { cursor, parentId, limit = 30 } = {}, signal) =>
  contentGet(parentId == null ? `/community/posts/${id}/comments/page` : `/community/posts/${id}/comments/${parentId}/replies/page`, {
    params: { cursor: cursor || undefined, limit }, signal, silent: true,
  });
export const callGetPost = (id) => contentGet(`/community/posts/${id}`);
export const callCreatePost = (payload) => muyeonClient.post("/community/posts", payload);
// 게시글 수정/삭제 — 본인 글만(서버가 403). 삭제는 soft delete 라 목록·상세에서 사라진다.
export const callUpdatePost = (id, payload) => muyeonClient.patch(`/community/posts/${id}`, payload);
export const callDeletePost = (id) => muyeonClient.delete(`/community/posts/${id}`);
// 익명 작성은 폐지(2026-10, Apple 1.2) — isAnonymous 를 보내지 않는다.
export const callCreateComment = (id, body, parentId) =>
  muyeonClient.post(`/community/posts/${id}/comments`, { body, parentId });
export const callUpdateComment = (id, commentId, body) =>
  muyeonClient.patch(`/community/posts/${id}/comments/${commentId}`, { body });
export const callDeleteComment = (id, commentId) =>
  muyeonClient.delete(`/community/posts/${id}/comments/${commentId}`);
export const callLikeComment = (id, commentId) =>
  muyeonClient.post(`/community/posts/${id}/comments/${commentId}/like`);
export const callScrapPost = (id) => withScrapToast(muyeonClient.post(`/community/posts/${id}/scrap`), true);
export const callUnscrapPost = (id) => withScrapToast(muyeonClient.delete(`/community/posts/${id}/scrap`), false);
export const callLikePost = (id) => muyeonClient.post(`/community/posts/${id}/like`);

// ── 공통(Phase 8): 지원내역·내공고·마감·신고·관심알림 ──

// 내 스크랩(좋아요) 목록 (관심 강사/무용수/공고/캐스팅) — targetType 옵션
export const callGetMyScraps = (targetType) =>
  muyeonClient.get("/me/scraps", { params: targetType ? { targetType } : {} });

// 좋아요(스크랩) 토글 — 누락분 보강 (구인공고/캐스팅/무용수 unscrap)
export const callScrapJob = (id) => withScrapToast(muyeonClient.post(`/jobs/${id}/scrap`), true);
export const callUnscrapJob = (id) => withScrapToast(muyeonClient.delete(`/jobs/${id}/scrap`), false);
export const callScrapCasting = (id) => withScrapToast(muyeonClient.post(`/casting/${id}/scrap`), true);
export const callUnscrapCasting = (id) => withScrapToast(muyeonClient.delete(`/casting/${id}/scrap`), false);
export const callUnscrapDancer = (id) => withScrapToast(muyeonClient.delete(`/dancers/${id}/scrap`), false);
export const callScrapSpace = (id) => withScrapToast(muyeonClient.post(`/spaces/${id}/scrap`), true);
export const callUnscrapSpace = (id) => withScrapToast(muyeonClient.delete(`/spaces/${id}/scrap`), false);
export const callScrapLesson = (id) => withScrapToast(muyeonClient.post(`/lesson-products/${id}/scrap`), true);
export const callUnscrapLesson = (id) => withScrapToast(muyeonClient.delete(`/lesson-products/${id}/scrap`), false);
export const callScrapPerformance = (id) => withScrapToast(muyeonClient.post(`/performances/${id}/scrap`), true);
export const callUnscrapPerformance = (id) => withScrapToast(muyeonClient.delete(`/performances/${id}/scrap`), false);

// 공지사항 / FAQ (운영자 admin 관리, 앱은 노출분 조회)
export const callGetNotices = () => muyeonClient.get(`/notices`);
export const callGetNotice = (id) => muyeonClient.get(`/notices/${id}`);
export const callGetFaqs = (category) => muyeonClient.get(`/faqs`, { params: category && category !== "ALL" ? { category } : {} });
export const callGetFaqCategories = () => muyeonClient.get(`/faqs/categories`);

// 내 지원 내역 (강사·무용수)
export const callGetMyApplications = () => contentGet("/me/applications");
// 확정 전 지원 취소. 기록은 삭제하지 않고 작성자 측에도 '지원 취소' 상태로 남긴다.
export const callWithdrawMyApplication = (kind, applicationId) =>
  muyeonClient.post(`/me/applications/${kind}/${applicationId}/withdraw`);
// 지원 취소가 아니라 내 목록에서만 숨긴다. 공고 작성자의 받은 지원과 결과 기록은 유지된다.
export const callArchiveMyApplication = (kind, applicationId) =>
  muyeonClient.delete(`/me/applications/${kind}/${applicationId}`);
// 내가 등록한 공고 (학원·공연팀)
export const callGetMyPostings = () => contentGet("/me/postings");
// 내 인증 대기(비공개) 공고 — 목록 맨 위에 작성자 본인에게만 보인다. kind = 'SUB' | 'JOB'.
//  목록 보조 영역이라 실패해도 화면을 막지 않는다(silent, 구 서버 404 → 호출부에서 빈 목록).
export const callGetMyPendingPostings = (kind) =>
  muyeonClient.get("/me/pending-postings", { params: { kind }, silent: true });
// 내 인증 대기 공고 요약 → { sub, job, total, roles:[{role,status,memo}] } — 일반회원 MY 인증 안내 팝업·메뉴용.
export const callGetMyPendingSummary = () =>
  muyeonClient.get("/me/pending-postings/summary", { silent: true });
// 인증 대기 공고의 공개 유형 바꾸기 — kind 'SUB'|'JOB', role(대타 TEACHER|ACADEMY, 채용 ACADEMY|TEAM) → { ok, role, published }
export const callRetargetPendingPosting = (kind, id, role) =>
  muyeonClient.patch(`/me/pending-postings/${kind}/${id}/role`, { role });
// 내 공간 (공간 보유 회원)
export const callGetMySpaces = () => contentGet("/me/spaces");
// 내 공간에 들어온 대관 문의 (공간 보유 회원)
export const callGetMySpaceInquiries = () => contentGet("/me/space-inquiries");
// 내 공간 예약 (소유자) + 예약 상태변경(확정/취소 → 예약자 알림)
export const callGetMySpaceReservations = () => contentGet("/me/space-reservations");
export const callUpdateReservationStatus = (resId, status) =>
  muyeonClient.patch(`/spaces/reservations/${resId}/status`, { status });
// 내가 신청한 예약 내역 (예약자) — 예약 확정/취소 알림 딥링크 대상
export const callGetMyReservations = () => contentGet("/me/reservations");
// 내 레슨 예약 목록(시간슬롯 예약)
export const callGetMyLessonReservations = () => contentGet("/me/lesson-reservations");

// ── 레슨 예약(슬롯 기반) 상세·취소·변경 ────────────────────────────
//  앱 LessonReservationDetailSheet / LessonBookingSheet 와 같은 API 를 웹에서도 쓴다.
//  ⚠️ 여기는 lesson_reservations 도메인이다. 견적에서 생기는 lesson_schedules(일정)와 다르다 —
//     캘린더의 일정이라도 source='BOOKING' 이면 일정 API 가 아니라 이쪽으로 보내야 한다
//     (서버 assertNotBooking 이 막는다). docs/web-lesson-parity-plan.md
export const callGetReservationDetail = (id) => muyeonClient.get(`/lesson-reservations/${id}/detail`);

// 취소 — reasonCode 는 constants/lessonCancelReasons.js 의 value(서버 화이트리스트와 계약).
//  reasonDetail 은 OTHER 일 때만 의미가 있고, 서버가 전화번호·이메일·욕설을 검사해 400 으로 거절한다
//  → 실패 시 서버 message 를 그대로 노출할 것.
export const callCancelReservation = (id, reasonCode, reasonDetail) =>
  muyeonClient.post(`/lesson-reservations/${id}/cancel`, { reasonCode, reasonDetail });

// 변경 요청 — 같은 레슨의 다른 회차를 고르면 강사/학원 승인 후 확정된다.
export const callRescheduleReservation = (id, slotInstanceId, headcount) =>
  muyeonClient.patch(`/lesson-reservations/${id}`, { slotInstanceId, headcount });

export const callOpenReservationDispute = (id, reason) =>
  muyeonClient.post(`/lesson-reservations/${id}/disputes`, { reason });

// 예약 가능한 회차(날짜 범위). 변경·신규 예약 화면에서 목록으로 쓴다.
export const callGetAvailableSlots = (lessonProductId, from, to) =>
  muyeonClient.get(`/lesson-products/${lessonProductId}/available-slots`, { params: { from, to } });

export const callCreateReservation = (slotInstanceId, headcount) =>
  muyeonClient.post("/lesson-reservations", { slotInstanceId, headcount });
export const callGetCompletedLessons = () => contentGet("/lessons/me/completed-lessons");

// 공고 마감 (등록자) — kind: 'JOB' | 'SUB' | 'CASTING'
export const callCloseJob = (id, rejectPending = false) =>
  muyeonClient.patch(`/jobs/${id}/close`, { rejectPending });
export const callCloseCasting = (id) => muyeonClient.patch(`/casting/${id}/close`);
const CLOSE_MAP = { JOB: callCloseJob, SUB: callCloseSub, CASTING: callCloseCasting };
export const callClosePosting = (kind, id, rejectPending = false) =>
  (CLOSE_MAP[kind] || callCloseJob)(id, rejectPending);
// 공고 삭제 = 보관(ARCHIVED) 소프트 처리. 지원 이력은 남는다.
export const callDeletePosting = (kind, id) => muyeonClient.delete(`/me/postings/${kind}/${id}`);
// 공고 상태 변경 (OPEN=다시 열기 / HOLD=보류 / CLOSED=마감)
export const callSetPostingStatus = (kind, id, status) =>
  muyeonClient.patch(`/me/postings/${kind}/${id}/status`, { status });
export const callDuplicatePosting = (kind, id) =>
  muyeonClient.post(`/me/postings/${kind}/${id}/duplicate`);
// 보관함 — 삭제(보관)한 공고 목록 + 복원. 삭제가 소프트라 되살릴 경로가 반드시 있어야 한다.
export const callGetArchivedPostings = () => muyeonClient.get("/me/postings/archived");
export const callEmptyArchivedPostings = () => muyeonClient.delete("/me/postings/archived");
export const callPurgeArchivedPosting = (kind, id) =>
  muyeonClient.delete(`/me/postings/${kind}/${id}/permanent`);
export const callRestorePosting = (kind, id) =>
  muyeonClient.post(`/me/postings/${kind}/${id}/restore`);

// 신고 — targetType: 'JOB'|'SUB'|'CASTING'|'RESUME'|'DANCER'|'TEACHER'|'POST'|'COMMENT'|'REVIEW'|'LESSON'|'CHAT_MESSAGE'
//  같은 대상을 다시 신고하면 서버가 200 으로 받아준다(중복 접수 아님).
export const callReport = (payload) => muyeonClient.post("/reports", payload);

// 사용자 차단(안전 기능 — Apple 1.2). 차단한 사용자의 글·댓글·채팅은 서버가 걸러 준다.
//  ⚠️ '추천 제외'(/quotes/recommend-blocks)와 다른 기능이다.
export const callBlockUser = (userId) => muyeonClient.post(`/users/${userId}/block`).then((result) => { invalidateCursorLists(); return result; });
export const callUnblockUser = (userId) => muyeonClient.delete(`/users/${userId}/block`).then((result) => { invalidateCursorLists(); return result; });
export const callGetMyBlocks = () => muyeonClient.get("/me/blocks");

// 작성자 본인 공고 삭제(상세 화면용). 내 공고 관리는 /me/postings 경로를 그대로 쓴다.
export const callDeleteJob = (id) => muyeonClient.delete(`/jobs/${id}`);
export const callDeleteCasting = (id) => muyeonClient.delete(`/casting/${id}`);

// 관심 조건 알림 (기획 11.7)
export const callGetAlertPrefs = () => muyeonClient.get("/me/alert-prefs");
export const callUpdateAlertPrefs = (payload) => muyeonClient.patch("/me/alert-prefs", payload);
// ★ 2026-11-02 '공고 알림 받기' 신청 — 채용·대타 새 공고 알림을 켠다(회원유형 없이도 수신). source = 누른 화면(JOB_BOARD 등).
export const callAlertOptIn = (source) => muyeonClient.post("/me/alert-prefs/opt-in", { source });

// ★ 2026-11-02 AI 이미지 자동입력(1인 1회 체험) — 기존 공고·이력서 사진으로 등록 칸을 채운다(저장은 화면이 한다).
//  config: { job:{enabled,used}, profile:{enabled,used} } — 비로그인도 부른다(used=false).
//  job/profile: 사진을 읽는 데 20~40초 걸려 timeout 을 길게, 전역 로딩·재시도 오버레이 대신 화면이 직접 로딩을 그린다(silent).
export const callGetAiAutofillConfig = () => muyeonClient.get("/ai-autofill/config", { silent: true });
export const callAiAutofill = (kind, imageUrls) =>
  muyeonClient.post(`/ai-autofill/${kind === "PROFILE" ? "profile" : "job"}`, { imageUrls }, { silent: true, timeout: 90000 });

// 강사 레슨 설정의 견적 수신 조건 — iOS QuotePrefsService 와 동일한 계약.
// 빈 배열은 해당 조건을 제한하지 않는다는 뜻이며, enabled=false 면 모든 요청을 받는다.
export const callGetQuotePrefs = () => muyeonClient.get("/me/quote-prefs");
export const callUpdateQuotePrefs = (payload) => muyeonClient.put("/me/quote-prefs", payload);

// 학원 기본정보 (학원명 + 원하는 강사 장르/분야) — 홈 '학원 맞춤 추천 강사' 소스
export const callGetAcademyProfile = () => muyeonClient.get("/auth/me/academy");
export const callUpdateAcademyProfile = (payload) => muyeonClient.patch("/auth/me/academy", payload);
// 학원 맞춤 추천 강사(내 원하는 장르·분야 매칭)
export const callGetRecommendedTeachers = (signal) => muyeonClient.get("/teachers/recommended", { signal });

// 약관·정책 문서 — 관리자(약관·정책)가 버전 관리. 현재 버전 + 시행 예정 + 이전 버전 목록. 비로그인 허용.
//  실패하면 pages/setting/Policy.js 가 constants/policyContent.js 문구로 폴백한다.
export const callGetPolicy = (docKey) => muyeonClient.get(`/policies/${encodeURIComponent(docKey)}`, { silent: true });
export const callGetPolicyVersion = (docKey, version) =>
  muyeonClient.get(`/policies/${encodeURIComponent(docKey)}/versions/${encodeURIComponent(version)}`, { silent: true });

// 레슨 예약금 환불 규정 + 예약금 한도 — 관리자(결제·수익화 > 환불 규정)가 정한 '지금 적용 중' 버전.
//  비로그인 허용. 실패해도 화면이 비면 안 된다 → hooks/useRefundPolicy 가 기본 문구로 폴백한다.
export const callGetRefundPolicy = () => muyeonClient.get("/refund-policy/lesson", { silent: true });

// 견적요청 (숨고식) — 문진 위저드(네이티브)에서 생성, 내역/받은견적은 웹
export const callCreateQuote = (payload) => muyeonClient.post("/quotes", payload);
export const callGetMyQuotes = () => muyeonClient.get("/quotes/me");
export const callGetQuote = (id) => muyeonClient.get(`/quotes/${id}`); // { quote, responses }
// 비회원 공개 상세(요청 내용 요약만 — 요청자·받은 견적·사진 제외). 2026-11-03 인스타 공유 링크용.
export const callGetPublicQuote = (id) => muyeonClient.get(`/public/quotes/${id}`, { silent: true }); // { quote, responseCount }
export const callGetHomeQuotes = () => muyeonClient.get("/quotes/home");
export const callScrapQuote = (id) => withScrapToast(muyeonClient.post(`/quotes/${id}/scrap`), true);
export const callUnscrapQuote = (id) => withScrapToast(muyeonClient.delete(`/quotes/${id}/scrap`), false);
// 무응답 요청 강사 추천("이런 강사는 어때요?") — { teachers: [프로필요약+reasons+alreadyRequested] }
export const callGetQuoteRecommendations = (id) => muyeonClient.get(`/quotes/${id}/recommendations`);
// 회원 앱 메뉴 — 활동유형별 노출을 관리자(admin /admin/app-menus)가 제어한다.
//  surface: MY | TAB | SHORTCUT. 활동유형은 인터셉터가 붙이는 X-Active-Type 헤더로 전달된다.
//  ⚠️ 실패해도 화면이 비면 안 된다. 호출부는 하드코딩 메뉴로 폴백한다.
export const callGetAppMenus = (surface = "MY") =>
  muyeonClient.get("/app-menus", { params: { surface }, silent: true });

// 강사 추천 제외("이 강사 다시 추천받지 않기") — 인스타 '이 광고 그만 보기' 류.
//  제외하면 홈·강사 목록·레슨 목록에서 사라지고, 내 견적요청도 그 강사에게 가지 않는다.
//  ⚠️ 채팅 차단(/users/:id/block)과 다른 기능이다. 차단=안전(괴롭힘), 제외=취향(피드).
export const callGetRecommendBlocks = () => muyeonClient.get("/quotes/recommend-blocks");
export const callBlockRecommendation = (teacherId) =>
  muyeonClient.post("/quotes/recommend-blocks", { teacherId: Number(teacherId) }).then((result) => { invalidateCursorLists(); return result; });
export const callUnblockRecommendation = (teacherId) =>
  muyeonClient.delete(`/quotes/recommend-blocks/${teacherId}`).then((result) => { invalidateCursorLists(); return result; });

// 추천 강사에게 기존 요청 그대로 지정 재요청(원터치) — { quoteId }
export const callQuoteReRequest = (id, teacherId, force = false) => muyeonClient.post(`/quotes/${id}/re-request`, { teacherId, force });
export const callCancelQuote = (id) => muyeonClient.patch(`/quotes/${id}/cancel`);
export const callDeleteQuote = (id) => muyeonClient.delete(`/quotes/${id}`); // 취소/마감된 요청 영구 삭제

// 레슨(클래스) 상품
export const callGetLessonsPage = (params = {}, signal) => contentGet("/lesson-products/page", {
  params: { ...params, limit: params.limit ?? 30, cursor: params.cursor || undefined }, signal, silent: true,
});
export const callGetHomeLessonPreview = (params = {}, signal) => contentGet("/lesson-products/home-preview", {
  params, signal, silent: true,
});
export const callGetLessons = (params = {}) => contentGet("/lesson-products", { params });
export const callGetLesson = (id) => contentGet(`/lesson-products/${id}`);
export const callGetMyLessons = () => muyeonClient.get("/lesson-products/mine");
export const callCreateLesson = (payload) => muyeonClient.post("/lesson-products", payload);
export const callUpdateLesson = (id, payload) => muyeonClient.put(`/lesson-products/${id}`, payload);
export const callDeleteLesson = (id) => muyeonClient.delete(`/lesson-products/${id}`);
export const callGetArchivedLessons = () => muyeonClient.get("/lesson-products/archived");
export const callRestoreLesson = (id) => muyeonClient.post(`/lesson-products/${id}/restore`);
// 수익화(유료 결제) — 상품 조회 / 주문 생성 / 내 이용권
export const callGetPlans = (featureType) =>
  muyeonClient.get("/monetization/plans", { params: featureType ? { featureType } : {} });
export const callCreateOrder = (payload) => muyeonClient.post("/monetization/orders", payload);
export const callGetMyEntitlements = () => muyeonClient.get("/monetization/entitlements/me");
// 내 멤버십 요약 — 등급·만료일·한도·이번 달 사용량. 판정은 서버가 한 번만 한다.
export const callGetMyMembership = () => muyeonClient.get("/monetization/membership/me");
// 구독(자동결제) — 카드 등록은 토스 창이 하고, 서버로는 인증키만 넘긴다(카드번호는 오지 않음).
export const callGetMySubscription = () => muyeonClient.get("/subscription/me");
export const callStartSubscription = (payload) => muyeonClient.post("/subscription", payload);
export const callCancelSubscription = () => muyeonClient.delete("/subscription");
export const callResumeSubscription = () => muyeonClient.post("/subscription/resume");
export const callChangeSubscriptionPlan = (planCode) =>
  muyeonClient.patch("/subscription/plan", { planCode });
export const callAcceptQuoteResponse = (quoteId, responseId) =>
  muyeonClient.post(`/quotes/${quoteId}/responses/${responseId}/accept`); // → { chatRoomId }
// 바로 채팅(수락 없이): 방 생성 + 견적카드/자동인사 삽입 → { roomId }
export const callStartQuoteChat = (quoteId, responseId) =>
  trackOnSuccess(muyeonClient.post(`/quotes/${quoteId}/responses/${responseId}/chat`), "cta:chat_start_quote", () => quoteId);
// 내 자동 인사 템플릿(관리자 설정) — 견적 보내기 폼 프리필
export const callGetMyChatAutoMessage = () => muyeonClient.get("/chat/my/auto-message");

// 강사 마이페이지: 자동견적 설정 조회/저장 (자동발송 ON/OFF·금액·메시지)
export const callGetMyAutoQuote = () => muyeonClient.get("/chat/my/auto-quote");
export const callUpdateMyAutoQuote = (payload) => muyeonClient.put("/chat/my/auto-quote", payload);

// 자동응답 템플릿 라이브러리(배민식: 여러 개 저장 → 활성 1개)
export const callGetAutoTemplates = () => muyeonClient.get("/chat/my/auto-quote/templates");
export const callCreateAutoTemplate = (payload) => muyeonClient.post("/chat/my/auto-quote/templates", payload);
export const callUpdateAutoTemplate = (id, payload) => muyeonClient.put(`/chat/my/auto-quote/templates/${id}`, payload);
export const callDeleteAutoTemplate = (id) => muyeonClient.delete(`/chat/my/auto-quote/templates/${id}`);
export const callActivateAutoTemplate = (id) => muyeonClient.post(`/chat/my/auto-quote/templates/${id}/activate`);

// 역할(다중) + 역할별 인증
export const callGetMyRoles = () => muyeonClient.get("/me/roles");
export const callAddRole = (role) => muyeonClient.post("/me/roles", { role });
export const callRemoveRole = (role) => muyeonClient.delete(`/me/roles/${role}`);
export const callCheckAcademyBusinessNumber = (businessRegistrationNumber, businessStartDate) =>
  muyeonClient.post("/me/roles/academy/business-number/check", {
    businessRegistrationNumber,
    ...(businessStartDate ? { businessStartDate } : {}),
  });
// businessStartDate(개업일자 YYYYMMDD) — 국세청 진위확인이 맞으면 서버가 바로 승인한다(2026-10-22).
export const callSubmitRoleVerification = (role, documents, academyName, businessRegistrationNumber, businessStartDate) =>
  muyeonClient.post(`/me/roles/${role}/verification`, {
    documents,
    ...(academyName ? { academyName } : {}),
    ...(businessRegistrationNumber ? { businessRegistrationNumber } : {}),
    ...(businessStartDate ? { businessStartDate } : {}),
  });
// 전문가(강사/학원) 측
export const callGetAvailableQuotes = (categoryId, page = 0, applyPrefs = true) =>
  muyeonClient.get("/quotes/available", {
    params: { ...(categoryId ? { categoryId } : {}), page, ...(applyPrefs ? {} : { applyPrefs: 0 }) },
  });
export const callSendQuoteResponse = (quoteId, payload) =>
  muyeonClient.post(`/quotes/${quoteId}/responses`, payload);
// 내가(강사·원장) 보낸 견적 목록 — 개인레슨 관리 '수강생 상담' 탭에서 문의와 한 줄기로 합쳐 쓴다.
//  (네이티브는 QuoteService.getSentQuotes 로 같은 엔드포인트를 쓴다)
export const callGetSentQuotes = () => muyeonClient.get("/quotes/sent");
// 자동견적 발송 내역(커서 방식) — month='YYYY-MM', before=직전 페이지 nextCursor(responseId), size=20.
//  요약(used/limit/remaining/skippedCount)은 첫 페이지(before 없음)에만 오고, 이후 페이지는 null.
export const callGetAutoSentQuotes = (month, before, size = 20) =>
  muyeonClient.get("/quotes/auto-sent", {
    params: { ...(month ? { month } : {}), ...(before != null ? { before } : {}), size },
  });
// 자동견적 횟수 소진으로 못 보낸 요청(커서 방식) — 요청자 정보는 오지 않는다(견적이 안 나갔으므로).
export const callGetAutoSkippedQuotes = (month, before, size = 20) =>
  muyeonClient.get("/quotes/auto-skipped", {
    params: { ...(month ? { month } : {}), ...(before != null ? { before } : {}), size },
  });

// 강사 리뷰·평점 (레슨 이용한 회원만 작성)
export const callGetTeacherReviews = (teacherId) =>
  muyeonClient.get(`/teachers/${teacherId}/reviews`); // { avg, count, canWrite, myReview, items }
// 후기 전체 화면 — 정렬(best|recent|rating_high|rating_low)·형태·목적·사진만 + 분포·필터 개수(iOS ReviewService.list 와 같음)
export const callGetTeacherReviewList = (teacherId, { sort, lessonType, purpose, photoOnly } = {}) =>
  muyeonClient.get(`/teachers/${teacherId}/reviews`, {
    params: { sort: sort || "best", lessonType: lessonType || undefined, purpose: purpose || undefined, photoOnly: photoOnly ? 1 : undefined },
  });
// 도움돼요 토글 → { liked, count }
export const callToggleReviewHelpful = (reviewId) => muyeonClient.post(`/reviews/${reviewId}/helpful`);
export const callWriteReview = (teacherId, rating, content) =>
  muyeonClient.post(`/reviews`, { teacherId, rating, content });
export const callDeleteMyReview = (teacherId) =>
  muyeonClient.delete(`/teachers/${teacherId}/reviews/mine`);

// 멤버십 성과 — 클라이언트는 실제 화면 노출·클릭만 전송하고,
// 견적·예약·완료·리뷰는 서버의 업무 원본 테이블에서 집계한다.
// 이용 행동 로그(화면·클릭·스크롤·유입) — util/appAnalytics.js 가 모아서 보낸다. 실패해도 화면은 그대로.
export const callPostAppEvents = (body) =>
  muyeonClient.post("/analytics/app-events", body, { silent: true });
export const callTrackPerformanceEvents = (events) =>
  muyeonClient.post("/analytics/events/batch", { events }, { silent: true });
export const callGetMyPerformance = (memberType, from, to) =>
  muyeonClient.get("/analytics/performance/me", { params: { memberType, from, to } });

// 고객문의 — 1:1 문의·의견을 운영 DB에 접수해 관리자페이지(/voc)에서 처리한다.
//  회원 ↔ 운영자 1:1 대화라 목록·상세 모두 본인 것만 본다.
//  ⚠️ 공개 Q&A 게시판을 접으면서 백엔드 라우트도 함께 걷어냈다:
//     GET /support-inquiries, GET /support-inquiries/public, POST /:id/unlock.
export const callCreateSupportInquiry = (type, content) =>
  muyeonClient.post("/support-inquiries", { type, content });
export const callGetMySupportInquiries = () => muyeonClient.get("/support-inquiries/mine");
export const callGetSupportInquiry = (id) => muyeonClient.get(`/support-inquiries/${id}`);
export const callDeleteSupportInquiry = (id) => muyeonClient.delete(`/support-inquiries/${id}`);

export default muyeonClient;

// ── 학원 ↔ 강사 소속 (학원 코드 → 강사 신청 → 학원 승인) ──
//  학원이 소속 강사 명의로 레슨을 내보내기 위한 관계. 신청만으로는 아무 권한도 생기지 않는다.
//  ⚠️ 강사를 검색해 초대하는 API(/invite, /accept, /reject)는 admin 전용이라 여기 두지 않는다.
export const callGetAcademyTeachers = (status) =>
  muyeonClient.get("/academy-teachers/mine", { params: status ? { status } : {} });
export const callGetAcademyInvites = (status) =>
  muyeonClient.get("/academy-teachers/invites", { params: status ? { status } : {} });
export const callGetAcademyCode = () => muyeonClient.get("/academy-teachers/code");
export const callReissueAcademyCode = () => muyeonClient.post("/academy-teachers/code/reissue");
export const callJoinAcademyByCode = (code) => muyeonClient.post("/academy-teachers/join", { code });
export const callApproveAcademyJoin = (id) => muyeonClient.post(`/academy-teachers/${id}/approve`);
export const callDenyAcademyJoin = (id) => muyeonClient.post(`/academy-teachers/${id}/deny`);
export const callCancelAcademyJoin = (id) => muyeonClient.post(`/academy-teachers/${id}/cancel`);
export const callLeaveAcademy = (id) => muyeonClient.post(`/academy-teachers/${id}/leave`);
// 관리자가 CS 대응으로 직접 연결한 건(status=INVITED)에 강사가 응답하는 경로.
//  앱에 초대 화면은 없지만, 이 응답까지 없으면 그 행이 영영 처리되지 않는 상태로 남는다.
export const callAcceptAcademyInvite = (id) => muyeonClient.post(`/academy-teachers/${id}/accept`);
export const callRejectAcademyInvite = (id) => muyeonClient.post(`/academy-teachers/${id}/reject`);

// Reservation history is filtered and sorted before applying the page boundary.
export const callGetReservationHistoryPage = ({ category, cursor } = {}, signal) =>
  contentGet("/me/lesson-reservations/history/page", { params: { category, cursor, limit: 30 }, signal, silent: true });
export const callGetCompletedLessonsPage = ({ cursor } = {}, signal) =>
  contentGet("/lessons/me/completed-lessons/page", { params: { cursor, limit: 30 }, signal, silent: true });

export const callGetTeacherHomePreview = (active, signal) => {
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  return contentGet("/home/teacher-preview", { params: { ...regionParams(active), regionName: active?.name, today }, signal, silent: true });
};

// Owner pages fetch summaries; full documents are loaded on demand.
export const callGetMyApplicationsPage = (params = {}, signal) => contentGet("/me/applications/page", { params: { ...params, limit: 30 }, signal, silent: true });
export const callGetMyPostingsPage = (params = {}, signal) => contentGet("/me/postings/page", { params: { ...params, limit: 30 }, signal, silent: true });
export const callGetArchivedPostingsPage = (params = {}, signal) => contentGet("/me/postings/archived/page", { params: { ...params, limit: 30 }, signal, silent: true });
export const callGetMyPosting = (kind, id, signal) => contentGet(`/me/postings/${kind}/${id}`, { signal, silent: true });

// AI 도우미 초안(앱 AI 작성 → 등록 화면 ?aiDraft=) — 받아서 칸 채우기, 저장하면 채택 기록.
export const callGetAiDraft = (id) => muyeonClient.get(`/assistant/drafts/${encodeURIComponent(id)}`);
export const callAiDraftOutcome = (id, body) => muyeonClient.post(`/assistant/drafts/${encodeURIComponent(id)}/outcome`, body);
